import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TylerState, useTylerStateUpdate } from '../utils/TylerState';
import scenarioService from '../services/scenario-service';
import StructuredData from './StructuredData';
import PWAInstallPrompt from './shared/PWAInstallPrompt';
import { useCountry } from '../contexts/CountryContext';

interface DashboardProps {
  user?: any;
  guestScenarios?: Record<string, any>;
}

interface RecentScenario {
  id: number;
  name: string;
  scenario_type: string;
  created_at: string;
  updated_at?: string;
}

const Dashboard: React.FC<DashboardProps> = ({ user, guestScenarios }) => {
  // Subscribe to TylerState updates for reactive user changes
  useTylerStateUpdate();
  const { isUSA } = useCountry();

  const currentUser = TylerState.user;
  const hasGuestScenarios = guestScenarios && Object.keys(guestScenarios).length > 0;
  const navigate = useNavigate();
  const adminEmail = process.env.REACT_APP_ANALYTICS_ADMIN_EMAIL || 'tylerhargrave@gmail.com';
  const isAnalyticsAdmin = !!currentUser && typeof currentUser.email === 'string' && currentUser.email.toLowerCase() === adminEmail.toLowerCase();

  // State for recent database scenarios
  const [recentScenarios, setRecentScenarios] = useState<RecentScenario[]>([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);

  // Load recent scenarios from database for logged-in users
  useEffect(() => {
    const loadRecentScenarios = async () => {
      if (!currentUser) return;

      try {
        setLoadingScenarios(true);
        const scenarios = await scenarioService.getUserScenarios();

        // Sort by updated_at (or created_at as fallback) and take top 5
        const sorted = scenarios
          .sort((a, b) => {
            const dateA = new Date(a.updated_at || a.created_at).getTime();
            const dateB = new Date(b.updated_at || b.created_at).getTime();
            return dateB - dateA;
          })
          .slice(0, 5);

        setRecentScenarios(sorted);
      } catch (error) {
        console.error('Failed to load recent scenarios:', error);
      } finally {
        setLoadingScenarios(false);
      }
    };

    loadRecentScenarios();
  }, [currentUser]);

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Larry's Help</h1>
        <p>Your comprehensive financial planning platform</p>
      </div>

      <div className="dashboard-content">
        {/* Launch Promo Banner - Show for guests and non-supporters */}
        {(!TylerState.user || TylerState.user.tier !== 'supporter') && (
          <div className="promo-banner launch-promo">
            <div className="promo-content">
              <span className="promo-badge">🎉 LAUNCH SPECIAL - Get 50% Off Supporters Club!</span>
              <p>Use code <strong>LAUNCH50</strong> at checkout - Just $5/year (normally $10)</p>
              <Link to="/account" className="promo-cta">Upgrade Now →</Link>
            </div>
          </div>
        )}

        {/* PWA Install Banner */}
        <PWAInstallPrompt variant="banner" />

        {/* Quick Actions */}
        <div className="quick-actions">
          <div className="action-cards">
            {/* Analytics (admin only, shown first) */}
            {isAnalyticsAdmin && (
              <div
                className="action-card"
                role="link"
                tabIndex={0}
                onClick={() => navigate('/analytics')}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/analytics')}
              >
                <div className="card-icon">💎</div>
                <div className="card-content">
                  <h3>Analytics Dashboard</h3>
                  <p>View live usage, errors, and device/browser stats</p>
                </div>
              </div>
            )}

            {/* RRSP - Canada only */}
            {!isUSA && (
              <div
                className="action-card"
                role="link"
                tabIndex={0}
                onClick={() => navigate('/rrsp')}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/rrsp')}
              >
                <div className="card-icon">💰</div>
                <div className="card-content">
                  <h3>RRSP Calculator</h3>
                  <p>Advanced retirement planning with scenario simulation</p>
                  <Link
                    to="/retirement-planner-info"
                    className="learn-more-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            )}

            {/* US Retirement Planner - USA only */}
            {isUSA && (
              <div
                className="action-card"
                role="link"
                tabIndex={0}
                onClick={() => navigate('/us-retirement')}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/us-retirement')}
              >
                <div className="card-icon">🇺🇸</div>
                <div className="card-content">
                  <h3>US Retirement Planner</h3>
                  <p>401(k), IRA, Social Security with Monte Carlo simulation</p>
                  <Link
                    to="/retirement-planner-info"
                    className="learn-more-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            )}

            {/* Housing */}
            <div
              className="action-card"
              role="link"
              tabIndex={0}
              onClick={() => navigate('/housing')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/housing')}
            >
              <div className="card-icon">🏠</div>
              <div className="card-content">
                <h3>Rent vs Buy Calculator</h3>
                <p>Compare renting vs buying with detailed cost analysis</p>
                <Link
                  to="/housing-calculator-info"
                  className="learn-more-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Personal Forecast */}
            <div
              className="action-card"
              role="link"
              tabIndex={0}
              onClick={() => navigate('/forecast')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/forecast')}
            >
              <div className="card-icon">📈</div>
              <div className="card-content">
                <h3>Personal Forecast</h3>
                <p>Personal finance planning and budget forecasting</p>
                <Link
                  to="/personal-forecast-info"
                  className="learn-more-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Tax & Payroll Tools (combined) */}
            <div
              className="action-card"
              role="link"
              tabIndex={0}
              onClick={() => navigate('/tax-tools')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/tax-tools')}
            >
              <div className="card-icon">🧾</div>
              <div className="card-content">
                <h3>{isUSA ? 'Income Tax Estimator' : 'Tax & Payroll Tools'}</h3>
                <p>{isUSA
                  ? 'Estimate federal and state income tax'
                  : 'Estimate income tax or calculate payroll deductions'
                }</p>
                <Link
                  to="/tax-tools-info"
                  className="learn-more-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Car Comparison */}
            <div
              className="action-card"
              role="link"
              tabIndex={0}
              onClick={() => navigate('/car-comparison')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/car-comparison')}
            >
              <div className="card-icon">🚗</div>
              <div className="card-content">
                <h3>Car Comparison</h3>
                <p>Compare buying, financing, leasing, or not having a car</p>
                <Link
                  to="/car-calculator-info"
                  className="learn-more-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Career Comparison */}
            <div
              className="action-card"
              role="link"
              tabIndex={0}
              onClick={() => navigate('/career-comparison')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/career-comparison')}
            >
              <div className="card-icon">🎓</div>
              <div className="card-content">
                <h3>Career Comparison</h3>
                <p>Compare staying in your current career vs. getting an education</p>
                <Link
                  to="/career-comparison-info"
                  className="learn-more-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Scheduling Platform */}
            <div
              className="action-card"
              role="link"
              tabIndex={0}
              onClick={() => navigate('/scheduling')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/scheduling')}
            >
              <div className="card-icon">📅</div>
              <div className="card-content">
                <h3>Scheduling Platform</h3>
                <p>Free booking links, calendar sync, and meeting scheduling</p>
                <Link
                  to="/scheduling-info"
                  className="learn-more-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Accounting System */}
            <div
              className="action-card"
              role="link"
              tabIndex={0}
              onClick={() => navigate('/accounting')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/accounting')}
            >
              <div className="card-icon">📒</div>
              <div className="card-content">
                <h3>Accounting System</h3>
                <p>Double-entry bookkeeping with journal entries and financial reports</p>
                <Link
                  to="/accounting-info"
                  className="learn-more-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Larry Line - Anti-Tanking Draft Tracker */}
            <div
              className="action-card"
              role="link"
              tabIndex={0}
              onClick={() => navigate('/larry-line/nhl')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/larry-line/nhl')}
            >
              <div className="card-icon">🏒</div>
              <div className="card-content">
                <h3>The Larry Line</h3>
                <p>Anti-tanking draft tracker for NHL and NBA. See what the draft would look like if losing wasn't rewarded.</p>
                <Link
                  to="/larry-line-info"
                  className="learn-more-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Scenarios - Database for logged-in users, localStorage for guests */}
        {currentUser && recentScenarios.length > 0 && (
          <div className="recent-scenarios">
            <h2 style={{"color":"white"}}>Recent Scenarios</h2>
            <div className="scenario-cards">
              {recentScenarios.map((scenario) => {
                const getRouteFromType = (type: string) => {
                  const routes: Record<string, string> = {
                    'housing': 'housing',
                    'retirement': 'rrsp',
                    'car': 'car-comparison',
                    'career': 'career-comparison'
                  };
                  return routes[type] || type;
                };

                const getIconFromType = (type: string) => {
                  const icons: Record<string, string> = {
                    'housing': '🏠',
                    'retirement': '💰',
                    'car': '🚗',
                    'career': '🎓'
                  };
                  return icons[type] || '📊';
                };

                const getDisplayName = (type: string) => {
                  const names: Record<string, string> = {
                    'housing': 'Housing',
                    'retirement': 'Retirement',
                    'car': 'Car Comparison',
                    'career': 'Career Comparison'
                  };
                  return names[type] || type;
                };

                return (
                  <div key={scenario.id} className="scenario-card">
                    <div className="scenario-type">
                      {getIconFromType(scenario.scenario_type)} {scenario.name}
                    </div>
                    <div className="scenario-time">
                      {scenario.updated_at ? `Updated ${new Date(scenario.updated_at).toLocaleDateString()}` : `Created ${new Date(scenario.created_at).toLocaleDateString()}`}
                    </div>
                    <Link to={`/${getRouteFromType(scenario.scenario_type)}`} className="scenario-link">
                      Continue Scenario
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Guest scenarios from localStorage */}
        {!currentUser && hasGuestScenarios && (
          <div className="recent-scenarios">
            <h2 style={{"color":"white"}}>Recent Scenarios</h2>
            <div className="scenario-cards">
              {Object.entries(guestScenarios).map(([type, scenario]) => (
                <div key={type} className="scenario-card">
                  <div className="scenario-type">
                    {type === 'housing' && '🏠 Housing'}
                    {type === 'rrsp' && '💰 Retirement'}
                    {type === 'forecast' && '📈 Forecast'}
                    {type === 'car-comparison' && '🚗 Car Comparison'}
                    {type === 'career-comparison' && '🎓 Career Comparison'}
                  </div>
                  <div className="scenario-time">
                    Saved {new Date(scenario.timestamp || Date.now()).toLocaleDateString()}
                  </div>
                  <Link to={`/${type}`} className="scenario-link">
                    Continue Scenario
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* User Welcome */}
        {TylerState.user && (
          <div className="user-welcome">
            <h2>Welcome back, {TylerState.user.email}!</h2>
            <div className="user-stats">
              <div className="stat-card">
                {TylerState.user.scenarios?.length || 0} Saved Scenarios
              </div>
              <div className="stat-card">
                  {TylerState.user.tier === 'free' ? (
                    <>
                      Free Account <Link to="/account" className="upgrade-link">Upgrade</Link>
                    </>
                  ) : TylerState.user.tier === 'supporter' ? (
                    'Supporters Club ⭐'
                  ) : (
                    'Guest'
                  )}
              </div>
            </div>
          </div>
        )}

        {/* Guest Mode Info */}
        {!TylerState.user && (
          <div className="guest-info">
            <h2>Getting Started</h2>
            <div className="info-cards">
              <div className="info-card">
                <h3>🎯 Try Our Tools</h3>
                <p>Start with any calculator above. No registration required for basic use.</p>
              </div>
              <Link to="/account" className="info-card">
                <h3>💾 Save Your Work</h3>
                <p>Create a free account to save your scenarios and access them later.</p>
              </Link>
              <Link to="/account" className="info-card">
                <h3>⭐ Go Pro</h3>
                <p>Join our Supporters Club for unlimited scenarios and PDF exports.</p>
              </Link>
            </div>
          </div>
        )}

        {/* Pricing FAQ Schema */}
        <StructuredData
          type="faq"
          data={{
            faqs: [
              {
                question: "Is Larry's Help free to use?",
                answer: "Yes! Larry's Help offers free Canadian financial calculators, free bookkeeping for one business, and free scheduling. For unlimited scenarios, bookkeeping for up to 10 businesses, and scheduling Pro features, join the Supporters Club for just $10 CAD per year with no auto-renewal."
              },
              {
                question: "How much does the Supporters Club cost?",
                answer: "The Supporters Club costs $10 CAD per year - less than $1/month. We keep pricing simple, transparent, and affordable. No hidden fees, no auto-renewal, no pressure. Just unlimited scenarios and advanced features to help you make better financial decisions."
              },
              {
                question: "Does Larry's Help auto-renew my subscription?",
                answer: "No! We don't believe in auto-renewal. When you join the Supporters Club for $10/year, you stay in control. We'll remind you before expiration, but you choose whether to renew. Your money, your choice."
              },
              {
                question: "What's included in the $10 Supporters Club?",
                answer: "One membership covers everything: unlimited saved scenarios and advanced calculator features, bookkeeping for up to 10 businesses with 10,000 journal entries, scheduling Pro features (hide branding, API keys, staff logins, payment collection), and priority support. All for less than $1/month with no auto-renewal."
              }
            ]
          }}
        />

        {/* Footer Message */}
        <div className="dashboard-footer">
          <p>Access powerful financial tools to make informed decisions about your future.</p>
          <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
            <a href="/privacy-policy" style={{ color: '#3b82f6', textDecoration: 'none' }}>Privacy Policy</a>
            {' | '}
            <a href="/terms-of-service" style={{ color: '#3b82f6', textDecoration: 'none' }}>Terms of Service</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 